import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ContentTypeBadge,
  EmptyState,
  Page,
  StatusBadge,
} from "../../../components/ui";
import {
  AnalyticsPanel,
  CrewList,
  DetailHeader,
  DetailSkeleton,
  DetailTabs,
  FactList,
  MediaPlayer,
  OnOffBadge,
  TagList,
  useMediaAnalytics,
  useStoredDetail,
} from "../../../components/detail";
import { getSeasonList } from "../../../services/shows";
import { getDate } from "../../../utils/format";
import { capitalize, stripHtml } from "../../../utils/list";
import { canViewAnalytics } from "../../../utils/roles";

/** JW media ids of the episodes of `seasonId` ("all": every season). */
export const episodeMediaIds = (seasons, seasonId) =>
  (seasons || [])
    .filter((season) => seasonId === "all" || season._id === seasonId)
    .flatMap((season) => season.episodes || [])
    .map((episode) => episode.media_id)
    .filter(Boolean);

/**
 * Details page shared by TV shows and podcasts.
 *
 * - `idParam`: name of the route param holding the series id
 * - `load(id)`: fetches the series
 * - `storeKey` / `setInfo`: uploadMediaReducer field and its action creator
 * - `pagePath(id)`: path of this page; leaving it clears the stored series
 * - `tagSuffix`: JW tag suffix hidden from the tag list (e.g. "-tvshow")
 * - `showTrailer`: shows the trailer player (TV shows only)
 */
const SeriesInfoPage = ({
  idParam,
  load,
  storeKey,
  setInfo,
  pagePath,
  tagSuffix,
  showTrailer,
}) => {
  const { t } = useTranslation();
  const seriesId = useParams()[idParam];
  const allowedAdmins = canViewAnalytics();

  const { item: series, loading } = useStoredDetail({
    load: () => load(seriesId),
    setInfo,
    storeKey,
    pagePath: pagePath(seriesId),
  });

  const [seasonList, setSeasonList] = useState([]);
  useEffect(() => {
    getSeasonList(seriesId).then((data) => setSeasonList(data?.seasons || []));
  }, [seriesId]);

  const [season, setSeason] = useState("all");
  const mediaIds = useMemo(
    () => episodeMediaIds(series?.seasons, season),
    [series, season]
  );
  const analytics = useMediaAnalytics(
    mediaIds,
    series?.createdAt,
    allowedAdmins
  );

  const [tab, setTab] = useState("crewmembers");

  if (loading) {
    return (
      <Page>
        <DetailSkeleton />
      </Page>
    );
  }

  if (!series) {
    return (
      <Page>
        <EmptyState icon="ri-film-line">
          {t("No data found for the provided ID!")}
        </EmptyState>
      </Page>
    );
  }

  const seasons = series.seasons || [];
  const episodeCount = seasons.reduce(
    (count, s) => count + (s.episodes || []).length,
    0
  );

  const tabs = [
    ...(allowedAdmins
      ? [{ id: "description", label: t("Analytics"), icon: "ri-bar-chart-2-line" }]
      : []),
    { id: "crewmembers", label: capitalize(t("crew mem")), icon: "ri-group-line" },
  ];

  const description = (
    <section className="ui-card detail-pad">
      <div className="detail-section">
        <h3 className="detail-section-title">{t("desc")}</h3>
        <p className="detail-description">
          {stripHtml(series.description) || "—"}
        </p>
      </div>
      <TagList
        label={t("Genre")}
        items={(series.genre || []).map((genre) => genre.name)}
      />
      <TagList
        label={t("tags")}
        items={(series.jw_tags || []).filter((tag) => !tag.endsWith(tagSuffix))}
      />
      <TagList label={t("Seo Keywords")} items={series.seo_tags} />
    </section>
  );

  return (
    <Page>
      <DetailHeader
        title={series.title}
        badges={
          <>
            <StatusBadge active={Boolean(series.status)} />
            <ContentTypeBadge type={series.content_type} />
          </>
        }
      />

      <div className="detail-layout">
        <div className="detail-stack">
          {showTrailer && (
            <MediaPlayer
              mediaId={series.trailer?.media_id}
              emptyText={t("Trailer has not been uploaded yet")}
            />
          )}
          {description}
        </div>

        <section className="ui-card detail-pad">
          {series.thumbnail?.static_thumbnail_url && (
            <img
              className="detail-poster"
              src={series.thumbnail.static_thumbnail_url}
              alt=""
            />
          )}
          <FactList
            items={[
              { label: capitalize(t("seasons")), value: seasons.length },
              { label: t("Episodes"), value: episodeCount },
              { label: t("availability"), value: series.availability },
              {
                label: t("Release Date"),
                value: getDate(series.release_year),
              },
              { label: t("Default Language"), value: series.default_language },
              {
                label: t("Monetization"),
                value: <OnOffBadge on={Boolean(series.monetization)} />,
              },
            ]}
          />
        </section>
      </div>

      <section className="ui-card">
        <DetailTabs tabs={tabs} value={tab} onChange={setTab} />
        <div className="detail-tab-panel">
          {allowedAdmins && tab === "description" && (
            <AnalyticsPanel
              {...analytics}
              emptyText={t("Episodes have not been uploaded yet!")}
              filters={
                <select
                  className="detail-select"
                  aria-label={t("Choose Season")}
                  value={season}
                  onChange={(e) => setSeason(e.target.value)}
                >
                  <option value="all">{t("Show All")}</option>
                  {seasonList.map((s, index) => (
                    <option key={s._id} value={s._id}>
                      {t("Season")} {index + 1}
                    </option>
                  ))}
                </select>
              }
            />
          )}
          {tab === "crewmembers" && <CrewList members={series.crew_members} />}
        </div>
      </section>
    </Page>
  );
};

export default SeriesInfoPage;
